import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  designations: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const designationSlice = createSlice({
  name: 'designation',
  initialState,
  reducers: {
    setDesignations: (state, action) => {
      state.designations = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setDesignations, setTotalCount, setTotalPages, setLoading, setError } = designationSlice.actions;

// Get designations (paginated & searchable)
export const getDesignations = (filters) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters?.page || 1;
    const limit = filters?.limit || 10;
    const search = filters?.search || '';
    const response = await axiosInstance.get('/admin/designation/list', {
      params: { page, limit, search }
    });
    if (response.data && response.data.status === true) {
      const result = response.data.data?.result || response.data.data || [];
      const totalCount = response.data.data?.totalCount || result.length;
      const totalPages = response.data.data?.totalPages || 1;
      dispatch(setDesignations(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setDesignations([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch designations';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setDesignations([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch designations';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single designation details
export const getDesignationDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/designation/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch designation details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch designation details';
    return { status: false, message: errorMsg };
  }
};

// Add new designation
export const addDesignation = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/designation/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add designation' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add designation';
    return { status: false, message: errorMsg };
  }
};

// Update designation details
export const updateDesignation = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/designation/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update designation' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update designation';
    return { status: false, message: errorMsg };
  }
};

// Delete designation
export const deleteDesignation = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/designation/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete designation' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete designation';
    return { status: false, message: errorMsg };
  }
};

export default designationSlice.reducer;
